import { ArrowRight } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Section, SectionHeading } from "./service-revops-ui";
import { SectionCta } from "./service-revops-section-cta";
import { gohighlevelConfig as defaultConfig } from "./service-platform-config";
import type { PlatformConfig } from "./service-platform-config";

export function ServiceCaseStudies({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  const caseStudies = config.caseStudies;
  return (
    <Section>
      <SectionHeading
        eyebrow={config.casesEyebrow}
        navy={config.casesHeadlineNavy}
        blue={config.casesHeadlineBlue}
      />
      <div className="grid md:grid-cols-2 gap-6">
        {caseStudies.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group relative flex flex-col rounded-[24px] p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
              border: "1px solid rgba(8,46,103,0.07)",
              boxShadow: "0 14px 40px rgba(8,46,103,0.08)",
            }}
          >
            <div
              className="absolute top-0 right-0 w-28 h-28 rounded-full blur-[50px] -mr-14 -mt-14 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(11,124,242,0.12)" }}
            />
            <h4
              className="font-display font-bold mb-2 group-hover:text-primary transition-colors relative z-10"
              style={{
                fontSize: "19px",
                color: "#071E61",
                letterSpacing: "-0.02em",
              }}
            >
              {c.title}
            </h4>
            <p
              className="text-sm leading-relaxed mb-5 relative z-10"
              style={{ color: "#536B89" }}
            >
              {c.desc}
            </p>
            <div
              className="flex items-center gap-1.5 text-sm font-medium mt-auto relative z-10"
              style={{ color: "#0B7CF2" }}
            >
              Read Guide
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

export function ServiceFaq({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  const faqs = config.faqs;
  return (
    <Section max="5xl">
      <SectionHeading
        eyebrow={config.faqEyebrow}
        navy={config.faqHeadlineNavy}
        blue={config.faqHeadlineBlue}
      />
      <div className="grid md:grid-cols-2 gap-4 items-start">
        {faqs.map((faq, idx) => (
          <details
            key={faq.question}
            className="group rounded-[22px] overflow-hidden transition-all duration-200"
            style={{
              background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
              border: "1px solid rgba(8,46,103,0.08)",
              boxShadow: "0 10px 30px rgba(8,42,102,0.06)",
            }}
          >
            <summary className="flex items-center justify-between gap-4 p-6 cursor-pointer list-none select-none transition-colors group-open:bg-[#F4FAFF]">
              <div className="flex items-center gap-3.5 flex-1 min-w-0 text-left">
                <span
                  className="font-display font-black text-[15px] shrink-0"
                  style={{ color: "#0B7CF2" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="w-px h-5 shrink-0"
                  style={{ background: "rgba(11,124,242,0.22)" }}
                />
                <h3
                  className="font-display font-bold group-hover:text-[#0B7CF2] transition-colors leading-[1.3]"
                  style={{
                    fontSize: "16.5px",
                    color: "#071E61",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {faq.question}
                </h3>
              </div>
              <div
                aria-hidden="true"
                className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-200 shadow-xs"
                style={{
                  background: "#EDF7FF",
                  color: "#0B7CF2",
                }}
              >
                <span className="block group-open:hidden leading-none">+</span>
                <span className="hidden group-open:block leading-none">
                  &minus;
                </span>
              </div>
            </summary>
            <div
              className="px-6 pb-6 pt-2 text-left border-t border-[rgba(8,46,103,0.05)]"
              style={{ background: "#FFFFFF" }}
            >
              <p
                style={{
                  fontSize: "14.5px",
                  lineHeight: 1.65,
                  color: "#536B89",
                }}
              >
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
      <SectionCta copy={config.faqCtaCopy} label={config.faqCtaLabel} />
    </Section>
  );
}
